import type { VariantProps } from "class-variance-authority";
import { createContext, useState, type ReactNode } from "react";
import { Dialog } from "./dialog.js";
import { DialogContent } from "./sections/dialog-content.js";
import { DialogDescription } from "./sections/dialog-description.js";
import { DialogFooter } from "./sections/dialog-footer.js";
import { DialogHeader } from "./sections/dialog-header.js";
import { DialogTitle } from "./sections/dialog-title.js";
import { Button } from "../button/button.js";

type DialogConfig = {
  title?: string;
  description?: string;
  content?: ReactNode;
  contentClassName?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: VariantProps<typeof Button>["variant"];
  cancelVariant?: VariantProps<typeof Button>["variant"];
  disableXClose?: boolean;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
};

type DialogState = {
  config: DialogConfig;
  isLoading: boolean;
};

type ImperativeDialogContext = {
  showDialog: (config: DialogConfig) => void;
  closeDialog: () => void;
  closeAllDialogs: () => void;
  updateCurrentDialog: (config: Partial<DialogConfig>) => void;
};

export const ImperativeDialogContext = createContext<ImperativeDialogContext | undefined>( // eslint-disable-line react-refresh/only-export-components
  undefined
);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogStack, setDialogStack] = useState<DialogState[]>([]);

  const showDialog = (dialogConfig: DialogConfig) => {
    setDialogStack((prev) => [...prev, { config: dialogConfig, isLoading: false }]);
  };

  const updateCurrentDialog = (config: Partial<DialogConfig>) => {
    setDialogStack((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((dialog, i) =>
        i === prev.length - 1 ? { ...dialog, config: { ...dialog.config, ...config } } : dialog
      );
    });
  };

  const closeDialog = () => {
    setDialogStack((prev) => prev.slice(0, -1));
  };

  const closeAllDialogs = () => {
    setDialogStack([]);
  };

  const setLoading = (index: number, loading: boolean) => {
    setDialogStack((prev) =>
      prev.map((dialog, i) => (i === index ? { ...dialog, isLoading: loading } : dialog))
    );
  };

  const handleConfirm = async (index: number) => {
    const dialog = dialogStack[index];
    if (dialog?.config.onConfirm) {
      setLoading(index, true);
      try {
        await dialog.config.onConfirm();
      } catch (error) {
        console.error("Dialog confirm error:", error);
      } finally {
        setLoading(index, false);
      }
    } else {
      closeDialog();
    }
  };

  const handleCancel = (index: number) => {
    const dialog = dialogStack[index];
    if (dialog?.config.onCancel) {
      dialog.config.onCancel();
    } else {
      closeDialog();
    }
  };

  return (
    <ImperativeDialogContext.Provider
      value={{ showDialog, closeDialog, closeAllDialogs, updateCurrentDialog }}
    >
      {children}

      {dialogStack.map((dialog, index) => (
        <Dialog
          key={index}
          open={true}
          onOpenChange={(open) => {
            if (!open && index === dialogStack.length - 1) {
              closeDialog();
            }
          }}
        >
          <DialogContent
            onClose={dialog.config.onClose}
            disableXClose={dialog.config.disableXClose}
            className={dialog.config.contentClassName}
          >
            <>
              {(dialog.config.title || dialog.config.description) && (
                <DialogHeader>
                  <DialogTitle>{dialog.config.title}</DialogTitle>
                  {dialog.config.description && (
                    <DialogDescription>{dialog.config.description}</DialogDescription>
                  )}
                </DialogHeader>
              )}

              {dialog.config.content && <div>{dialog.config.content}</div>}

              {(dialog.config.confirmText || dialog.config.cancelText) && (
                <DialogFooter>
                  {dialog.config.cancelText && (
                    <Button
                      data-allow-navigation
                      variant={dialog.config.cancelVariant || "outline"}
                      onClick={() => handleCancel(index)}
                      disabled={dialog.isLoading}
                    >
                      {dialog.config.cancelText}
                    </Button>
                  )}
                  {dialog.config.confirmText && (
                    <Button
                      data-allow-navigation
                      variant={dialog.config.confirmVariant}
                      onClick={() => handleConfirm(index)}
                      disabled={dialog.isLoading}
                    >
                      {dialog.isLoading ? "Loading..." : dialog.config.confirmText}
                    </Button>
                  )}
                </DialogFooter>
              )}
            </>
          </DialogContent>
        </Dialog>
      ))}
    </ImperativeDialogContext.Provider>
  );
};
